import { Num } from "@/components/Num";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { FileSpreadsheet, Building2 } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { KpiCards } from "@/components/KpiCards";
import { ConsolidationLines } from "@/components/ConsolidationLines";
import { Button } from "@/components/ui/button";
import { api, errorText, downloadFile } from "@/lib/api";
import { MONTHS, fmtCompact, fmtPct } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#059669", "#D97706", "#2563EB", "#7C3AED", "#DB2777", "#0891B2", "#65A30D", "#EA580C"];
const KPI_KEYS = [["revenue", "Total Revenue"], ["payroll", "Payroll"], ["gop", "GOP"], ["ebitda", "EBITDA"]];

export default function ConsolidationPage() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const { can } = useAuth();

  useEffect(() => {
    api.get("/consolidation", { params: year ? { year } : {} }).then((r) => { setData(r.data); if (!year) setYear(r.data.year); }).catch((e) => toast.error(errorText(e)));
  }, [year]);

  const exportXlsx = async () => {
    try { await downloadFile(`/consolidation/export?year=${year}`, `Armella_Group_Consolidated_${year}.xlsx`); toast.success("Consolidated Excel downloaded"); } catch (e) { toast.error(e.message); }
  };

  const props = data?.properties || [];
  const chart = MONTHS.map((m, i) => {
    const row = { name: m };
    props.forEach((p) => { row[p.hotel_name] = p.kpis.revenue?.budget?.[i] ?? 0; });
    return row;
  });

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AppNav />
      <main className="px-4 sm:px-8 lg:px-12 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 rise">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-emerald-700 font-semibold">Armella Group</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-2">Group Consolidation</h1>
            <p className="text-slate-600 mt-3 max-w-2xl text-sm sm:text-base">All property budgets rolled up into one USALI summary. The latest generated budget of each property for the selected year is used.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={year || ""} onChange={(e) => setYear(Number(e.target.value))} className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-mono" data-testid="consolidation-year-select">
              {(data?.available_years || []).map((y) => <option key={y} value={y}>{`Budget ${y}`}</option>)}
            </select>
            {can("export") && <Button onClick={exportXlsx} disabled={!props.length} className="bg-emerald-700 hover:bg-emerald-800" data-testid="consolidation-export-button"><FileSpreadsheet size={15} className="mr-2" /> Export Excel</Button>}
          </div>
        </div>

        {data && !props.length && (
          <div className="border border-dashed border-slate-300 rounded-lg p-14 text-center bg-white" data-testid="consolidation-empty">
            <Building2 className="mx-auto text-emerald-600" size={32} />
            <h3 className="font-heading text-xl font-semibold mt-4 text-slate-900">No generated budgets yet</h3>
            <p className="text-slate-500 text-sm mt-2">Generate a budget for at least one property to see the group roll-up.</p>
          </div>
        )}

        {props.length > 0 && (
          <>
            <KpiCards kpis={data.group} budgetYear={data.year} />
            <div className="grid xl:grid-cols-[1fr_1fr] gap-6">
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="consolidation-chart">
                <h3 className="font-heading text-base font-semibold text-slate-900">Monthly revenue budget {data.year} by property</h3>
                <div className="h-72 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart}>
                      <CartesianGrid vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 10, fill: "#64748B", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={64} />
                      <Tooltip formatter={(v) => fmtCompact(v)} contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: "#E2E8F0" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {props.map((p, i) => <Bar key={p.hotel_name} dataKey={p.hotel_name} stackId="rev" fill={COLORS[i % COLORS.length]} radius={i === props.length - 1 ? [3, 3, 0, 0] : 0} />)}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-auto" data-testid="consolidation-table">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[11px]">Property</th>
                      {KPI_KEYS.map(([k, l]) => <th key={k} className="text-right px-3 py-2.5 font-semibold text-[11px]">{l}</th>)}
                      <th className="text-right px-3 py-2.5 font-semibold text-[11px]">GOP %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.map((p) => (
                      <tr key={p.hotel_name} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`consolidation-row-${p.hotel_name.replace(/\s+/g, "-").toLowerCase()}`}>
                        <td className="px-4 py-2.5">
                          <Link to={`/projects/${p.project_id}`} className="font-medium text-slate-900 hover:text-emerald-700">{p.hotel_name}</Link>
                          <p className="text-[11px] text-slate-500">{p.project_name} · actuals thru {MONTHS[(p.last_actual_month || 1) - 1]} {p.actual_year}{p.approval_status === "approved" ? " · approved" : ""}</p>
                        </td>
                        {KPI_KEYS.map(([k]) => {
                          const it = p.kpis[k];
                          return <td key={k} className="px-3 py-2.5 text-right font-mono"><Num v={it?.budget_total} compact /><br /><span className={`text-[10px] ${(it?.var_pct || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(it?.var_pct)}</span></td>;
                        })}
                        <td className="px-3 py-2.5 text-right font-mono">{p.kpis.revenue?.budget_total && p.kpis.gop ? `${(p.kpis.gop.budget_total / p.kpis.revenue.budget_total * 100).toFixed(2)}%` : "-"}</td>
                      </tr>
                    ))}
                    <tr className="bg-emerald-50 font-semibold" data-testid="consolidation-group-total">
                      <td className="px-4 py-3 text-slate-900">Armella Group total</td>
                      {KPI_KEYS.map(([k]) => {
                        const it = data.group[k];
                        return <td key={k} className="px-3 py-3 text-right font-mono text-slate-900"><Num v={it?.budget_total} compact /><br /><span className={`text-[10px] ${(it?.var_pct || 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtPct(it?.var_pct)}</span></td>;
                      })}
                      <td className="px-3 py-3 text-right font-mono">{data.group.gop_margin_budget !== undefined ? `${data.group.gop_margin_budget.toFixed(2)}%` : "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <ConsolidationLines year={data.year} />
          </>
        )}
      </main>
    </div>
  );
}
